import { useEffect, useMemo, useState } from "react";
import { MessageSquarePlus, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { NoteItem } from "../components/NotesPanel";
import { NoteForm } from "../components/NoteForm";
import { useNotes } from "../lib/notesContext";
import { CATEGORIES } from "../lib/notes";
import { fetchDataset } from "../lib/api";

const trig = "h-9 bg-[#0B0F19] border-slate-700 text-slate-100 text-xs";

export default function NotesPage() {
  const { notes } = useNotes();
  const [pickers, setPickers] = useState([]);
  const [q, setQ] = useState("");
  const [who, setWho] = useState("all");
  const [cat, setCat] = useState("all");
  const [adding, setAdding] = useState(false);
  useEffect(() => { fetchDataset().then((d) => setPickers((d?.users || []).map((u) => u.id).sort())).catch(() => {}); }, []);
  const list = useMemo(() => notes.filter((n) =>
    (who === "all" || (who === "team" ? n.scope === "team" : n.picker === who)) &&
    (cat === "all" || n.category === cat) &&
    (!q || `${n.text} ${n.picker || ""} ${n.author}`.toLowerCase().includes(q.toLowerCase()))), [notes, who, cat, q]);
  const known = new Set(pickers);
  return (
    <main className="max-w-4xl mx-auto px-6 py-12 fade-up" data-testid="notes-page">
      <p className="label-mono">Commentary</p>
      <div className="flex flex-wrap items-end gap-3 mt-2">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-100 tracking-tight mr-auto">Notes</h1>
        <button data-testid="notes-add-button" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-md bg-ep hover:bg-ep-hover text-white"><MessageSquarePlus className="h-4 w-4" /> Add note</button>
      </div>
      <p className="text-slate-400 mt-3 max-w-2xl">Explain trends, dips and changes so the context isn't lost. Every note is pinned to calendar dates (a day, week, month, quarter or range), not to an upload, so it stays attached to the right period when the data is refreshed. Notes also appear on the dashboard timeline, the month spotlight, each picker's deep dive and the exec summary.</p>
      <div className="panel mt-8 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]"><Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-500" /><Input data-testid="notes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes" className={`${trig} pl-9`} /></div>
        <Select value={who} onValueChange={setWho}>
          <SelectTrigger data-testid="notes-filter-who" className={`${trig} w-[180px]`}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72"><SelectItem value="all">Team & pickers</SelectItem><SelectItem value="team">Team only</SelectItem>{[...new Set([...pickers, ...notes.filter((n) => n.picker).map((n) => n.picker)])].sort().map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="notes-filter-category" className={`${trig} w-[160px]`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All topics</SelectItem>{CATEGORIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-slate-500 font-mono" data-testid="notes-count">{list.length} notes</span>
      </div>
      <div className="mt-6 space-y-3" data-testid="notes-list">
        {list.length === 0 && <p className="text-sm text-slate-500">No notes yet. Add the first one to explain a trend.</p>}
        {list.map((n) => <NoteItem key={n.id} n={n} pickers={pickers} missing={n.picker && pickers.length > 0 && !known.has(n.picker)} />)}
      </div>
      {adding && <NoteForm open={adding} onOpenChange={setAdding} pickers={pickers} />}
    </main>
  );
}
